import { lazy, type ComponentType } from 'react';
import type { RouteObject } from 'react-router';
import RootLayout from '../components/layout/RootLayout';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import Catalog from '../pages/catalog/page';
import CatalogCategory from '../pages/catalog-category/page';
import Product from '../pages/product/page';
// Поиск — eager: из шапки ходят часто; lazy-чанк после деплоя давал «Не удалось загрузить» + reload
import Search from '../pages/search/page';

/** Lazy с одной повторной попыткой — смягчает сбой чанка после деплоя без полного reload */
function lazyRetry(
  factory: () => Promise<{ default: ComponentType<any> }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (first) {
      await new Promise((r) => setTimeout(r, 400));
      try {
        return await factory();
      } catch {
        throw first;
      }
    }
  });
}

const Home = lazyRetry(() => import('../pages/home/page'));
const Sets = lazyRetry(() => import('../pages/sets/page'));
const SetPage = lazyRetry(() => import('../pages/set/page'));
const Stores = lazyRetry(() => import('../pages/stores/page'));
const Delivery = lazyRetry(() => import('../pages/delivery/page'));
const Returns = lazyRetry(() => import('../pages/returns/page'));
const Rental = lazyRetry(() => import('../pages/rental/page'));
const Careers = lazyRetry(() => import('../pages/careers/page'));
const Documents = lazyRetry(() => import('../pages/documents/page'));
const Privacy = lazyRetry(() => import('../pages/privacy/page'));
const Oferta = lazyRetry(() => import('../pages/oferta/page'));
const Cookies = lazyRetry(() => import('../pages/cookies/page'));
const About = lazyRetry(() => import('../pages/about/page'));
const Contacts = lazyRetry(() => import('../pages/contacts/page'));
const Cart = lazyRetry(() => import('../pages/cart/page'));
const Checkout = lazyRetry(() => import('../pages/checkout/page'));
const Account = lazyRetry(() => import('../pages/account/page'));
const Login = lazyRetry(() => import('../pages/login/page'));
const Register = lazyRetry(() => import('../pages/register/page'));
const ForgotPassword = lazyRetry(() => import('../pages/forgot-password/page'));
const ResetPassword = lazyRetry(() => import('../pages/reset-password/page'));
const Favorites = lazyRetry(() => import('../pages/favorites/page'));
const Compare = lazyRetry(() => import('../pages/compare/page'));
const Orders = lazyRetry(() => import('../pages/orders/page'));
const OrderDetail = lazyRetry(() => import('../pages/orders/order-detail/page'));
const Collection = lazyRetry(() => import('../pages/collection/page.tsx'));
const NotFound = lazyRetry(() => import('../pages/NotFound'));

const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      { index: true, element: <Home /> },
      { path: 'catalog', element: <Catalog /> },
      { path: 'catalog/:category', element: <CatalogCategory /> },
      { path: 'collections/:slug', element: <Collection /> },
      { path: 'product/:slug', element: <Product /> },
      { path: 'sets', element: <Sets /> },
      { path: 'set/:id', element: <SetPage /> },
      { path: 'stores', element: <Stores /> },
      { path: 'delivery', element: <Delivery /> },
      { path: 'returns', element: <Returns /> },
      { path: 'rental', element: <Rental /> },
      { path: 'careers', element: <Careers /> },
      { path: 'documents', element: <Documents /> },
      { path: 'privacy', element: <Privacy /> },
      { path: 'oferta', element: <Oferta /> },
      { path: 'cookies', element: <Cookies /> },
      { path: 'about', element: <About /> },
      { path: 'contacts', element: <Contacts /> },
      { path: 'cart', element: <Cart /> },
      { path: 'checkout', element: <Checkout /> },
      { path: 'login', element: <Login /> },
      { path: 'register', element: <Register /> },
      { path: 'forgot-password', element: <ForgotPassword /> },
      { path: 'reset-password', element: <ResetPassword /> },
      {
        path: 'account',
        element: (
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        ),
      },
      { path: 'favorites', element: <Favorites /> },
      { path: 'compare', element: <Compare /> },
      {
        path: 'orders',
        element: (
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        ),
      },
      {
        path: 'orders/:id',
        element: (
          <ProtectedRoute>
            <OrderDetail />
          </ProtectedRoute>
        ),
      },
      { path: 'search', element: <Search /> },
      { path: '*', element: <NotFound /> },
    ],
  },
];

export default routes;
